import {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Download,
  File as FileIcon,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  Loader2,
  Paperclip,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import {
  fileService,
  moldAttachmentService,
  MoldAttachment,
  MoldAttachmentSyncItem,
} from '../../services/api';
import { formatShortThaiDateFromString } from '../../utils/dateUtils';
import Modal from './Modal';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

interface MoldAttachmentsProps {
  moldId: string;
}

const MAX_FILE_SIZE_MB = 16;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const ATTACHMENT_CATEGORIES = [
  {
    value: 'manual',
    label: 'คู่มือ',
    badgeClass: 'bg-blue-50 text-blue-700 ring-blue-200',
  },
  {
    value: 'drawing',
    label: 'Drawing',
    badgeClass: 'bg-violet-50 text-violet-700 ring-violet-200',
  },
  {
    value: 'certificate',
    label: 'ใบรับรอง',
    badgeClass: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  },
  {
    value: 'report',
    label: 'รายงาน',
    badgeClass: 'bg-amber-50 text-amber-700 ring-amber-200',
  },
  {
    value: 'other',
    label: 'อื่นๆ',
    badgeClass: 'bg-gray-100 text-gray-600 ring-gray-200',
  },
];

const categoryOf = (attachment: MoldAttachment) =>
  ATTACHMENT_CATEGORIES.find(
    (option) => option.value === attachment.category,
  ) ?? ATTACHMENT_CATEGORIES[ATTACHMENT_CATEGORIES.length - 1];

const displayName = (attachment: MoldAttachment) =>
  attachment.title || attachment.originalFileName || attachment.fileName;

const fileExtension = (attachment: MoldAttachment) =>
  (
    attachment.fileType ||
    attachment.fileName.slice(attachment.fileName.lastIndexOf('.'))
  )
    .toLowerCase()
    .replace(/^\./, '');

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'jfif',
  'png',
  'gif',
  'webp',
  'bmp',
  'heic',
  'heif',
  'tif',
  'tiff',
]);

const extensionOfName = (name: string) =>
  name.slice(name.lastIndexOf('.')).toLowerCase().replace(/^\./, '');

// Default display name suggested during upload: the file name without its
// extension.
const defaultTitle = (name: string) => name.replace(/\.[^.]+$/, '');

const visualForExt = (ext: string) => {
  if (ext === 'pdf')
    return { Icon: FileText, className: 'bg-red-50 text-red-500' };
  if (['doc', 'docx'].includes(ext))
    return { Icon: FileText, className: 'bg-blue-50 text-blue-500' };
  if (['xls', 'xlsx', 'csv'].includes(ext))
    return {
      Icon: FileSpreadsheet,
      className: 'bg-emerald-50 text-emerald-600',
    };
  if (['ppt', 'pptx'].includes(ext))
    return { Icon: FileText, className: 'bg-orange-50 text-orange-500' };
  if (IMAGE_EXTENSIONS.has(ext))
    return { Icon: FileImage, className: 'bg-violet-50 text-violet-500' };
  if (['zip', 'rar', '7z'].includes(ext))
    return { Icon: FileArchive, className: 'bg-amber-50 text-amber-600' };
  return { Icon: FileIcon, className: 'bg-gray-100 text-gray-500' };
};

const fileVisual = (attachment: MoldAttachment) =>
  visualForExt(fileExtension(attachment));

const formatFileSize = (bytes?: number | null) => {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const mutationErrorMessage = (error: unknown, fallback: string) => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 403) return 'ไม่มีสิทธิ์แก้ไขไฟล์แนบแม่พิมพ์';
  if (status === 409)
    return 'ข้อมูลไฟล์แนบมีการเปลี่ยนแปลง กรุณาโหลดใหม่แล้วลองอีกครั้ง';
  return fallback;
};

const useMoldAttachments = (moldId: string) => {
  const [attachments, setAttachments] = useState<MoldAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const reload = useCallback(async () => {
    if (!Number(moldId)) return;
    setError(false);
    try {
      const result = await moldAttachmentService.list(moldId);
      setAttachments(result.data || []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [moldId]);
  useEffect(() => {
    const load = async () => reload();
    void load();
  }, [reload]);
  return { attachments, setAttachments, loading, error, reload };
};

const CategoryPicker = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (category: string) => void;
}) => (
  <div className='flex flex-wrap gap-2'>
    {ATTACHMENT_CATEGORIES.map((option) => (
      <button
        key={option.value}
        type='button'
        onClick={() => onChange(option.value)}
        className={`rounded-full px-3 py-1.5 text-sm font-medium ring-1 transition ${
          value === option.value
            ? 'bg-blue-600 text-white ring-blue-600'
            : 'bg-white text-gray-600 ring-gray-300 hover:bg-gray-50'
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

interface EditAttachmentDialogProps {
  attachment: MoldAttachment;
  onClose: () => void;
  onSave: (title: string, category: string) => Promise<void>;
}

const EditAttachmentDialog = ({
  attachment,
  onClose,
  onSave,
}: EditAttachmentDialogProps) => {
  const [title, setTitle] = useState(attachment.title || '');
  const [category, setCategory] = useState(categoryOf(attachment).value);
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(title.trim(), category);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal isOpen onClose={onClose}>
      <div className='p-6'>
        <div className='mb-4 flex items-center justify-between'>
          <h2 className='text-xl font-semibold'>แก้ไขรายละเอียดไฟล์</h2>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 focus:outline-none'
            aria-label='ปิด'
          >
            <X size={24} />
          </button>
        </div>
        <p className='mb-4 truncate text-xs text-gray-500'>
          ไฟล์: {attachment.originalFileName || attachment.fileName}
        </p>
        <label
          htmlFor='attachment-edit-title'
          className='mb-1 block text-sm font-medium text-gray-700'
        >
          ชื่อที่แสดง
        </label>
        <input
          id='attachment-edit-title'
          type='text'
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={attachment.originalFileName || attachment.fileName}
          className='mb-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
        />
        <span className='mb-1 block text-sm font-medium text-gray-700'>
          หมวดหมู่
        </span>
        <div className='mb-6'>
          <CategoryPicker value={category} onChange={setCategory} />
        </div>
        <div className='flex justify-end gap-2'>
          <button
            onClick={onClose}
            className='rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          >
            ยกเลิก
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className='inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60'
          >
            {saving && <Loader2 size={16} className='animate-spin' />}
            บันทึก
          </button>
        </div>
      </div>
    </Modal>
  );
};

// One file waiting in the upload dialog, with its editable display metadata.
export interface PendingUploadEntry {
  file: File;
  title: string;
  category: string;
}

interface UploadDetailsDialogProps {
  entries: PendingUploadEntry[];
  uploading: boolean;
  errorMessage: string | null;
  onPickFiles: () => void;
  onUpdate: (index: number, patch: Partial<PendingUploadEntry>) => void;
  onRemove: (index: number) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

// The upload flow lives in this dialog: pick files, name and categorize each
// one, then confirm. Nothing is uploaded until the confirm button is pressed.
const UploadDetailsDialog = ({
  entries,
  uploading,
  errorMessage,
  onPickFiles,
  onUpdate,
  onRemove,
  onCancel,
  onConfirm,
}: UploadDetailsDialogProps) => (
  <Modal isOpen onClose={onCancel}>
    <div className='p-6'>
      <div className='mb-1 flex items-center justify-between'>
        <h2 className='text-xl font-semibold'>อัปโหลดไฟล์แนบ</h2>
        <button
          onClick={onCancel}
          disabled={uploading}
          className='text-gray-400 hover:text-gray-600 focus:outline-none'
          aria-label='ปิด'
        >
          <X size={24} />
        </button>
      </div>
      <p className='mb-4 text-sm text-gray-500'>
        ตั้งชื่อและเลือกหมวดหมู่ เพื่อให้ทีมค้นหาเอกสารได้ง่ายขึ้น
      </p>
      {errorMessage && (
        <div
          role='alert'
          className='mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600'
        >
          {errorMessage}
        </div>
      )}
      {entries.length === 0 ? (
        <button
          type='button'
          onClick={onPickFiles}
          className='flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/60 py-10 text-center transition hover:border-blue-300 hover:bg-blue-50/40'
        >
          <span className='rounded-full bg-blue-100 p-3 text-blue-600'>
            <Upload size={22} />
          </span>
          <span className='text-sm font-medium text-gray-700'>
            เลือกไฟล์จากเครื่อง
          </span>
          <span className='text-xs text-gray-400'>
            หรือลากไฟล์มาวาง · ไม่เกิน {MAX_FILE_SIZE_MB}MB ต่อไฟล์
          </span>
        </button>
      ) : (
        <>
          <div className='-mx-2 max-h-[55vh] space-y-3 overflow-y-auto px-2'>
            {entries.map((entry, index) => {
              const { Icon, className } = visualForExt(
                extensionOfName(entry.file.name),
              );
              return (
                <div
                  key={`${entry.file.name}-${index}`}
                  className='rounded-lg border border-gray-200 p-3'
                >
                  <div className='mb-2 flex items-center gap-2'>
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${className}`}
                    >
                      <Icon size={18} />
                    </span>
                    <span className='min-w-0 flex-1'>
                      <span className='block truncate text-sm font-medium text-gray-700'>
                        {entry.file.name}
                      </span>
                      <span className='block text-xs text-gray-400'>
                        {formatFileSize(entry.file.size) || ''}
                      </span>
                    </span>
                    <button
                      type='button'
                      onClick={() => onRemove(index)}
                      disabled={uploading}
                      className='rounded-md p-1.5 text-gray-300 transition hover:bg-red-50 hover:text-red-500'
                      aria-label='นำไฟล์ออก'
                      title='นำไฟล์ออก'
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <label
                    htmlFor={`attachment-upload-title-${index}`}
                    className='mb-1 block text-xs font-medium text-gray-600'
                  >
                    ชื่อที่แสดง
                  </label>
                  <input
                    id={`attachment-upload-title-${index}`}
                    type='text'
                    value={entry.title}
                    onChange={(event) =>
                      onUpdate(index, { title: event.target.value })
                    }
                    placeholder={defaultTitle(entry.file.name)}
                    className='mb-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
                  />
                  <span className='mb-1 block text-xs font-medium text-gray-600'>
                    หมวดหมู่
                  </span>
                  <CategoryPicker
                    value={entry.category}
                    onChange={(category) => onUpdate(index, { category })}
                  />
                </div>
              );
            })}
          </div>
          <button
            type='button'
            onClick={onPickFiles}
            disabled={uploading}
            className='mt-3 inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-blue-600 transition hover:bg-blue-50 disabled:opacity-60'
          >
            <Plus size={16} />
            เพิ่มไฟล์
          </button>
        </>
      )}
      <div className='mt-5 flex justify-end gap-2'>
        <button
          onClick={onCancel}
          disabled={uploading}
          className='rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60'
        >
          ยกเลิก
        </button>
        <button
          onClick={onConfirm}
          disabled={uploading || entries.length === 0}
          className='inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60'
        >
          {uploading ? (
            <Loader2 size={16} className='animate-spin' />
          ) : (
            <Upload size={16} />
          )}
          {uploading
            ? 'กำลังอัปโหลด...'
            : entries.length === 0
              ? 'อัปโหลด'
              : `อัปโหลด ${entries.length} ไฟล์`}
        </button>
      </div>
    </div>
  </Modal>
);

const MoldAttachments = ({ moldId }: MoldAttachmentsProps) => {
  const { attachments, setAttachments, loading, error, reload } =
    useMoldAttachments(moldId);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [editing, setEditing] = useState<MoldAttachment | null>(null);
  const [deleting, setDeleting] = useState<MoldAttachment | null>(null);
  // null = dialog closed; [] = dialog open awaiting file selection.
  const [pending, setPending] = useState<PendingUploadEntry[] | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const filterOptions = useMemo(() => {
    const counts = new Map<string, number>();
    attachments.forEach((attachment) => {
      const key = categoryOf(attachment).value;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return ATTACHMENT_CATEGORIES.filter((option) =>
      counts.has(option.value),
    ).map((option) => ({ ...option, count: counts.get(option.value) || 0 }));
  }, [attachments]);

  // Fall back to "all" when the selected category no longer exists (e.g. its
  // last file was deleted) instead of syncing state in an effect.
  const effectiveFilter =
    activeFilter === 'all' ||
    filterOptions.some((option) => option.value === activeFilter)
      ? activeFilter
      : 'all';

  const visibleAttachments = useMemo(
    () =>
      effectiveFilter === 'all'
        ? attachments
        : attachments.filter(
            (attachment) => categoryOf(attachment).value === effectiveFilter,
          ),
    [attachments, effectiveFilter],
  );

  const openUploadDialog = () => {
    if (uploading) return;
    setMutationError(null);
    setPending((current) => current ?? []);
  };

  // Add picked/dropped files to the upload dialog (opening it if needed).
  // Nothing is uploaded until the user confirms names/categories.
  const selectFiles = (incoming: File[]) => {
    if (!incoming.length || uploading) return;
    setMutationError(null);
    const oversize = incoming.filter((file) => file.size > MAX_FILE_SIZE_BYTES);
    const accepted = incoming.filter(
      (file) => file.size <= MAX_FILE_SIZE_BYTES,
    );
    if (oversize.length) {
      setMutationError(
        `ไฟล์เกิน ${MAX_FILE_SIZE_MB}MB จึงไม่ถูกอัปโหลด: ${oversize
          .map((file) => file.name)
          .join(', ')}`,
      );
    }
    if (inputRef.current) inputRef.current.value = '';
    if (!accepted.length && !oversize.length) return;
    setPending((current) => {
      const existing = current ?? [];
      const fresh = accepted
        .filter(
          (file) =>
            !existing.some(
              (entry) =>
                entry.file.name === file.name && entry.file.size === file.size,
            ),
        )
        .map((file) => ({
          file,
          title: defaultTitle(file.name),
          category: 'other',
        }));
      return [...existing, ...fresh];
    });
  };

  const updatePending = (index: number, patch: Partial<PendingUploadEntry>) => {
    setPending((current) =>
      (current ?? []).map((entry, i) =>
        i === index ? { ...entry, ...patch } : entry,
      ),
    );
  };

  const removePending = (index: number) => {
    setPending((current) => (current ?? []).filter((_, i) => i !== index));
  };

  // Upload the confirmed files, then register them as attachments with the
  // chosen title/category in one sync call.
  const confirmUpload = async () => {
    const entries = pending || [];
    const files = entries.map((entry) => entry.file);
    if (!files.length || uploading) return;
    setUploading(true);
    setMutationError(null);
    try {
      const { data } = await fileService.uploadFiles(
        'mold',
        Number(moldId),
        files,
      );
      // Statuses come back in the same order the files were sent; each carries
      // the server-side (cleaned) file name.
      const statuses = data || [];
      const syncItems: MoldAttachmentSyncItem[] = [];
      const failedNames: string[] = [];
      statuses.forEach((status, index) => {
        const original = files[index];
        if (status.error || !status.fileName) {
          failedNames.push(
            original?.name || status.fileName || 'ไม่ทราบชื่อไฟล์',
          );
          return;
        }
        const entry = entries[index];
        syncItems.push({
          fileName: status.fileName,
          title:
            entry?.title.trim() ||
            (original ? defaultTitle(original.name) : null),
          category: entry?.category || 'other',
        });
      });
      if (failedNames.length) {
        setMutationError(`อัปโหลดไม่สำเร็จ: ${failedNames.join(', ')}`);
      }
      if (syncItems.length) {
        const records = await moldAttachmentService.sync(moldId, syncItems);
        setAttachments(records.data || []);
      }
      setPending(null);
    } catch (uploadError) {
      setMutationError(
        mutationErrorMessage(
          uploadError,
          'ไม่สามารถอัปโหลดไฟล์ได้ กรุณาลองอีกครั้ง',
        ),
      );
      setPending(null);
      await reload();
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectFiles(Array.from(event.target.files || []));
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragActive(false);
    selectFiles(Array.from(event.dataTransfer.files || []));
  };

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLElement>) => {
    // Ignore leave events fired when moving between child elements.
    if (event.currentTarget.contains(event.relatedTarget as Node)) return;
    setDragActive(false);
  };

  const openOrDownload = async (attachment: MoldAttachment) => {
    setMutationError(null);
    try {
      const response = await fileService.downloadFile(attachment.fileName);
      const ext = fileExtension(attachment);
      const previewable = ext === 'pdf' || IMAGE_EXTENSIONS.has(ext);
      const url = URL.createObjectURL(response.data);
      if (previewable) {
        window.open(url, '_blank', 'noopener');
        // Give the new tab time to load the blob before revoking.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachment.originalFileName || attachment.fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      setMutationError('ไม่สามารถเปิดไฟล์ได้ กรุณาลองอีกครั้ง');
    }
  };

  const downloadFile = async (attachment: MoldAttachment) => {
    setMutationError(null);
    try {
      const response = await fileService.downloadFile(attachment.fileName);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachment.originalFileName || attachment.fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      setMutationError('ไม่สามารถดาวน์โหลดไฟล์ได้ กรุณาลองอีกครั้ง');
    }
  };

  const saveEdit = async (title: string, category: string) => {
    if (!editing) return;
    setMutationError(null);
    try {
      // The backend PATCH coalesces nulls, so an emptied title falls back to
      // the original file name instead of being unset.
      const nextTitle = title || editing.originalFileName || editing.fileName;
      await moldAttachmentService.update(moldId, editing.id, {
        title: nextTitle,
        category,
      });
      setAttachments(
        attachments.map((attachment) =>
          attachment.id === editing.id
            ? { ...attachment, title: nextTitle, category }
            : attachment,
        ),
      );
      setEditing(null);
    } catch (editError) {
      setMutationError(
        mutationErrorMessage(
          editError,
          'ไม่สามารถบันทึกรายละเอียดไฟล์ได้ กรุณาลองอีกครั้ง',
        ),
      );
      setEditing(null);
      await reload();
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setMutationError(null);
    const result = await fileService.deleteFile(deleting.fileName);
    setDeleting(null);
    if (!result.isSuccess) {
      setMutationError('ไม่สามารถลบไฟล์ได้ กรุณาลองอีกครั้ง');
      return;
    }
    // The attachment row is removed by FK cascade when the file is deleted.
    setAttachments(
      attachments.filter((attachment) => attachment.id !== deleting.id),
    );
  };

  if (loading) {
    return (
      <div className='py-12 text-center text-gray-500'>กำลังโหลดไฟล์แนบ...</div>
    );
  }
  if (error) {
    return (
      <div className='rounded-lg bg-red-50 px-4 py-3 text-red-600'>
        ไม่สามารถโหลดไฟล์แนบแม่พิมพ์ได้
      </div>
    );
  }

  return (
    <section
      aria-labelledby='mold-attachments-title'
      className='scroll-mt-4'
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type='file'
        multiple
        className='hidden'
        onChange={handleInputChange}
        aria-label='เลือกไฟล์แนบ'
      />
      <div className='mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <div>
          <h2
            id='mold-attachments-title'
            className='flex items-center gap-2 text-xl font-bold text-gray-900'
          >
            <Paperclip size={21} className='text-blue-600' />
            ไฟล์แนบ
            {attachments.length > 0 && (
              <span className='rounded-full bg-gray-100 px-2 py-0.5 text-sm font-medium text-gray-500'>
                {attachments.length}
              </span>
            )}
          </h2>
          <p className='mt-1 text-sm text-gray-500'>
            เอกสารประจำแม่พิมพ์ เช่น คู่มือ, Drawing, ใบรับรอง หรือรายงาน
            เพื่อใช้เป็นคลังความรู้ของทีม
          </p>
        </div>
        <button
          type='button'
          onClick={openUploadDialog}
          disabled={uploading}
          title={`รองรับไฟล์ทุกประเภท ขนาดไม่เกิน ${MAX_FILE_SIZE_MB}MB ต่อไฟล์`}
          className='inline-flex shrink-0 items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60'
        >
          {uploading ? (
            <Loader2 size={16} className='animate-spin' />
          ) : (
            <Upload size={16} />
          )}
          {uploading ? 'กำลังอัปโหลด...' : 'อัปโหลดไฟล์'}
        </button>
      </div>

      {mutationError && pending === null && (
        <div
          role='alert'
          className='mb-4 rounded-lg bg-red-50 px-4 py-3 text-red-600'
        >
          {mutationError}
        </div>
      )}

      {attachments.length > 0 && filterOptions.length > 1 && (
        <div className='mb-4 flex flex-wrap gap-2'>
          <button
            type='button'
            onClick={() => setActiveFilter('all')}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ring-1 transition ${
              effectiveFilter === 'all'
                ? 'bg-blue-600 text-white ring-blue-600'
                : 'bg-white text-gray-600 ring-gray-300 hover:bg-gray-50'
            }`}
          >
            ทั้งหมด ({attachments.length})
          </button>
          {filterOptions.map((option) => (
            <button
              key={option.value}
              type='button'
              onClick={() => setActiveFilter(option.value)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium ring-1 transition ${
                effectiveFilter === option.value
                  ? 'bg-blue-600 text-white ring-blue-600'
                  : 'bg-white text-gray-600 ring-gray-300 hover:bg-gray-50'
              }`}
            >
              {option.label} ({option.count})
            </button>
          ))}
        </div>
      )}

      {attachments.length === 0 ? (
        <button
          type='button'
          onClick={openUploadDialog}
          className={`flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed py-10 text-center transition ${
            dragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-200 bg-gray-50/60 hover:border-blue-300 hover:bg-blue-50/40'
          }`}
        >
          <span
            className={`rounded-full bg-white p-3 shadow-sm ${
              dragActive ? 'text-blue-500' : 'text-gray-300'
            }`}
          >
            <Paperclip size={26} />
          </span>
          <p className='text-sm font-medium text-gray-600'>
            {dragActive ? 'วางไฟล์เพื่ออัปโหลด' : 'ยังไม่มีไฟล์แนบ'}
          </p>
          {!dragActive && (
            <p className='text-xs text-gray-400'>
              ลากไฟล์มาวาง หรือกดเพื่อเลือกไฟล์ · ไม่เกิน {MAX_FILE_SIZE_MB}MB
              ต่อไฟล์
            </p>
          )}
        </button>
      ) : (
        <ul
          className={`divide-y divide-gray-100 overflow-hidden rounded-xl border transition ${
            dragActive
              ? 'border-blue-400 ring-2 ring-blue-200'
              : 'border-gray-200'
          }`}
        >
          {visibleAttachments.map((attachment) => {
            const { Icon, className } = fileVisual(attachment);
            const category = categoryOf(attachment);
            const size = formatFileSize(attachment.fileSize);
            const actualFileName =
              attachment.originalFileName || attachment.fileName;
            const metaParts = [
              // Show the underlying file name when a display name masks it.
              attachment.title && attachment.title !== actualFileName
                ? actualFileName
                : null,
              size,
              attachment.uploadedAt
                ? formatShortThaiDateFromString(attachment.uploadedAt)
                : null,
              attachment.uploadedByName
                ? `โดย ${attachment.uploadedByName}`
                : null,
            ].filter(Boolean);
            return (
              <li
                key={attachment.id}
                className='group flex items-center gap-3 bg-white px-3 py-3 transition hover:bg-blue-50/40 sm:px-4'
              >
                <button
                  type='button'
                  onClick={() => void openOrDownload(attachment)}
                  className='flex min-w-0 flex-1 items-center gap-3 text-left'
                  title='เปิดไฟล์'
                >
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${className}`}
                  >
                    <Icon size={22} />
                  </span>
                  <span className='min-w-0'>
                    <span className='block truncate text-sm font-semibold text-gray-800 group-hover:text-blue-700'>
                      {displayName(attachment)}
                    </span>
                    <span className='mt-0.5 block truncate text-xs text-gray-500'>
                      {metaParts.join(' · ')}
                    </span>
                  </span>
                </button>
                <span
                  className={`hidden shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ring-1 sm:inline-block ${category.badgeClass}`}
                >
                  {category.label}
                </span>
                <span className='flex shrink-0 items-center gap-1'>
                  <button
                    type='button'
                    onClick={() => void downloadFile(attachment)}
                    className='rounded-md p-2 text-gray-400 transition hover:bg-blue-50 hover:text-blue-600'
                    aria-label='ดาวน์โหลดไฟล์'
                    title='ดาวน์โหลด'
                  >
                    <Download size={18} />
                  </button>
                  <button
                    type='button'
                    onClick={() => setEditing(attachment)}
                    className='rounded-md p-2 text-gray-400 transition hover:bg-blue-50 hover:text-blue-600'
                    aria-label='แก้ไขรายละเอียดไฟล์'
                    title='แก้ไข'
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    type='button'
                    onClick={() => setDeleting(attachment)}
                    className='rounded-md p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600'
                    aria-label='ลบไฟล์'
                    title='ลบ'
                  >
                    <Trash2 size={18} />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {dragActive && attachments.length > 0 && (
        <p className='mt-2 text-center text-xs font-medium text-blue-600'>
          วางไฟล์เพื่ออัปโหลด
        </p>
      )}

      {pending !== null && (
        <UploadDetailsDialog
          entries={pending}
          uploading={uploading}
          errorMessage={mutationError}
          onPickFiles={() => inputRef.current?.click()}
          onUpdate={updatePending}
          onRemove={removePending}
          onCancel={() => {
            if (!uploading) setPending(null);
          }}
          onConfirm={() => void confirmUpload()}
        />
      )}
      {editing && (
        <EditAttachmentDialog
          attachment={editing}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}
      {deleting && (
        <DeleteConfirmationDialog
          setShowDeleteConfirm={() => setDeleting(null)}
          text={`คุณแน่ใจหรือไม่ว่าต้องการลบไฟล์ “${displayName(deleting)}”? ไฟล์จะถูกลบออกจากระบบอย่างถาวร`}
          confirmText='ลบไฟล์'
          onConfirm={() => void confirmDelete()}
        />
      )}
    </section>
  );
};

export default MoldAttachments;
